import { lazy } from 'react';

// Components for code splitting & lazy loading
// https://reactjs.org/docs/code-splitting.html

/**
 * Wrapper around React.lazy that retries on chunk load failure.
 *
 * When CloudFront signed cookies expire, chunk requests return 403.
 * Instead of crashing to the ErrorBoundary, this triggers a page reload
 * which re-runs the signer flow (via error/403.html) and comes back
 * with fresh cookies.
 *
 * Loop prevention: uses sessionStorage to avoid infinite reload if the
 * signer is broken.
 */
const CHUNK_RETRY_KEY = 'chunkLoadRetry';

type LazyImportFn = () => Promise<{ default: React.ComponentType<any> }>;

function lazyWithRetry(importFn: LazyImportFn) {
	return lazy(() =>
		importFn().catch((error: Error) => {
			const hasRetried = sessionStorage.getItem(CHUNK_RETRY_KEY);

			if (!hasRetried) {
				sessionStorage.setItem(CHUNK_RETRY_KEY, 'true');
				window.location.reload();

				return new Promise(() => {});
			}

			throw error;
		})
	);
}

if (sessionStorage.getItem(CHUNK_RETRY_KEY)) {
	sessionStorage.removeItem(CHUNK_RETRY_KEY);
}

// Legacy workflow pages
export const PreLaunchWorkflow = lazyWithRetry(
	() => import('src/examples/PreLaunchWorkflow')
);
export const CreateP2RRequest = lazyWithRetry(
	() => import('src/pages/P2RWorkflow/CreateP2RRequest')
);
export const RADInitialPage = lazyWithRetry(
	() => import('src/pages/RADWorkflow/RADInitialPage')
);
export const ReselienceInitialPage = lazyWithRetry(
	() => import('src/pages/ResilienceWorkflow/ResilienceInitialPage')
);
export const DockmasterRequest = lazyWithRetry(
	() => import('src/pages/DockmasterWorkflow/DockmasterRequest')
);
export const FCReconfigWorkflow = lazyWithRetry(
	() => import('src/pages/FCReconfigWorkflow/FCReconfigWorkflow')
);
export const FCReconfigNodeDetails = lazyWithRetry(
	() => import('src/pages/FCReconfigNodeDetails/FCReconfigNodeDetails')
);
export const SorterSetupWorkflowExecution = lazyWithRetry(
	() =>
		import(
			'src/pages/SorterSetupWorkflowExecution/SorterSetupWorkflowExecution'
		)
);
export const SorterSetupWorkflowRequirementPortal = lazyWithRetry(
	() =>
		import(
			'src/pages/SorterSetupWorkflowRequirementPortal/SorterSetupWorkflowRequirementPortal'
		)
);
export const NodeAddressUpdateWorkflow = lazyWithRetry(
	() => import('src/pages/NodeAddressUpdateWorkflow/NodeAddressUpdateWorkflow')
);
export const AddressUpdateWorkflowNodeDetails = lazyWithRetry(
	() =>
		import(
			'src/pages/AddressUpdateWorkflowNodeDetails/AddressUpdateWorkflowNodeDetails'
		)
);
export const CreateAnchorPointNodes = lazyWithRetry(
	() => import('src/pages/AnchorPointWorkflow/CreateAnchorPointNodes')
);
export const DeleteAnchorPointNodes = lazyWithRetry(
	() => import('src/pages/AnchorPointWorkflow/DeleteAnchorPointNodes')
);

// App Config pages
export const AppConfigReviewDashboard = lazyWithRetry(
	() => import('src/pages/appConfigReviewDashboard/appConfigReviewDashboard')
);
export const ListAppConfigReviews = lazyWithRetry(
	() => import('src/pages/listAppConfigReviews/listAppConfigReviews')
);
export const ViewAppConfigReview = lazyWithRetry(
	() => import('src/pages/viewAppConfigReview/viewAppConfigReview')
);
export const ViewHostedAppConfig = lazyWithRetry(
	() => import('src/pages/viewHostedAppConfig/viewHostedAppConfig')
);
export const OnBoardServiceAppConfig = lazyWithRetry(
	() => import('src/pages/onBoardServiceAppConfig/onBoardServiceAppConfig')
);
export const ViewFCToPodMappings = lazyWithRetry(
	() => import('src/pages/viewFCToPodMappings/viewFCToPodMappings')
);
export const AlertNodesDashboard = lazyWithRetry(
	() => import('src/pages/alertNodesDashboard/alertNodesDashboard')
);

// New pages
export const TaskConfiguration = lazyWithRetry(
	() => import('src/components/TaskConfiguration/TaskConfiguration')
);
export const NodeNameValidator = lazyWithRetry(
	() => import('src/pages/NodeNameValidator/NodeNameValidator')
);
export const WorkflowDashboard = lazyWithRetry(
	() => import('src/pages/WorkflowDashboard/WorkflowDashboard')
);
export const SimulateWorkflow = lazyWithRetry(
	() => import('src/pages/SimulateWorkflow/SimulateWorkflow')
);
export const WorkflowCompare = lazy(() => import('src/pages/WorkflowCompare/WorkflowCompare'));
export const ManageExecution = lazyWithRetry(
	() => import('src/pages/manageExecution/ManageExecution')
);
export const StartExecution = lazyWithRetry(
	() => import('src/pages/startExecution/StartExecution')
);

// Notification pages
export const NotificationManagerNavigation = lazyWithRetry(
	() => import('src/pages/Notification/NotificationManagerNavigation')
);
export const ViewNotifications = lazyWithRetry(
	() => import('src/pages/Notification/ViewNotifications')
);

// Attribute Management
export const AttributeManagementNavigation = lazyWithRetry(
	() =>
		import('src/components/AttributeManagement/AttributeManagementNavigation')
);
